import React, { useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { DateRangePicker as ReactDateRangePicker } from 'react-date-range';
import { format, isBefore, isSameDay } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useOutsideClick from 'hooks/useOutsideClick';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import './overrides.css';
import { RANGES, initialRange } from './ranges';

const DateRangePicker = ({ range, className, onApply }) => {
  const ref = useRef();
  const [tempRange, setTempRange] = useState([range]);
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState(null);
  const isCustom = !tempRange[0].isDefinedRange || tempRange[0].id === 4;

  const onClose = () => {
    setIsOpen(false);
    setType(null);
  };

  useOutsideClick(ref, () => {
    if (!isOpen) return;
    onClose();
    setTempRange([range]);
  });

  const _onApply = () => {
    if (onApply) {
      onApply(tempRange[0]);
    }
    onClose();
  };

  const setTempRangeConditionally = (_range, isDefinedRange = false) => {
    const id =
      RANGES.find(
        (item) =>
          isSameDay(
            new Date(item.range().startDate),
            new Date(_range.selection.startDate)
          ) &&
          isSameDay(
            new Date(item.range().endDate),
            new Date(_range.selection.endDate)
          )
      )?.range()?.id || RANGES[RANGES.length - 1]?.range()?.id;

    if (!isDefinedRange) {
      if (type === 'startDate') {
        if (isBefore(tempRange[0].endDate, _range.selection.startDate)) {
          // reset end date to start date
          setTempRange([{ ..._range.selection, isDefinedRange, id }]);
        } else {
          setTempRange([
            {
              ..._range.selection,
              endDate: tempRange[0].endDate,
              isDefinedRange,
              id,
            },
          ]);
        }
        setType('endDate');
      } else {
        setTempRange([
          {
            ..._range.selection,
            startDate: tempRange[0].startDate,
            isDefinedRange,
            id,
          },
        ]);
      }
    } else {
      if (type !== 'startDate') {
        setType('startDate');
      }
      setTempRange([{ ..._range.selection, isDefinedRange, id }]);
    }
  };

  const onOpen = (_type) => {
    if (type !== _type) {
      setType(_type);
      if (!isOpen) {
        setIsOpen(true);
      }
    }
    if (isOpen && type === _type) {
      onClose();
    }
  };

  const focusedRange = useMemo(() => {
    if (isCustom) {
      return type === 'startDate' ? [0, 0] : [0, 1];
    }
  }, [isCustom, type]);

  const shownDate = useMemo(() => {
    return type === 'startDate' ? tempRange[0].startDate : tempRange[0].endDate;
  }, [tempRange, type]);

  const minDate = useMemo(() => {
    return type === 'endDate' && isCustom ? tempRange[0].startDate : undefined;
  }, [isCustom, tempRange, type]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <div
        className={`text-sm text-javelin-gray-light border border-javelin-gray-border rounded bg-white cursor-default w-72 flex justify-evenly items-center text-center h-10`}
      >
        <div
          onClick={() => onOpen('startDate')}
          className={`relative flex items-center justify-center w-full flex-grow-0 py-2 px-2 box-border ${
            type === 'startDate' ? 'ring-2 ring-brand-primary rounded' : ''
          }`}
          data-testid="clickable-start-date"
        >
          <FontAwesomeIcon
            icon={['far', 'calendar']}
            size="sm"
            className="fill-current my-auto"
          />
          <span className="ml-2.5 mr-1">
            {format(tempRange[0].startDate, 'dd MMM yyyy')}
          </span>
          <span className="flex-grow-0 h-full my-auto">
            <FontAwesomeIcon
              icon="chevron-down"
              size="xs"
              className="text-javelin-gray-dark"
            />
          </span>
        </div>
        <span className="mx-1">-</span>
        <div
          onClick={() => onOpen('endDate')}
          className={`relative flex items-center justify-center w-full flex-grow-0 py-2 px-2 ${
            type === 'endDate' ? 'ring-2 ring-brand-primary rounded' : ''
          }`}
          data-testid="clickable-end-date"
        >
          <FontAwesomeIcon
            icon={['far', 'calendar']}
            size="sm"
            className="fill-current my-auto"
          />
          <span className="ml-2.5 mr-1">
            {format(tempRange[0].endDate, 'dd MMM yyyy')}
          </span>
          <span className="flex-grow-0 h-full my-auto">
            <FontAwesomeIcon
              icon="chevron-down"
              size="xs"
              className="text-javelin-gray-dark"
            />
          </span>
        </div>
      </div>
      {isOpen ? (
        <div
          className="absolute top-12 right-0 w-screen md:w-auto z-30"
          data-testid="date-range-picker"
        >
          <ReactDateRangePicker
            ranges={tempRange}
            months={2}
            inputRanges={null}
            staticRanges={RANGES}
            onChange={setTempRangeConditionally}
            focusedRange={focusedRange}
            direction="horizontal"
            weekStartsOn={1}
            showMonthAndYearPickers={false}
            showDateDisplay={false}
            fixedHeight
            keepFocusedRange={!tempRange[0].isDefinedRange}
            shownDate={shownDate}
            minDate={minDate}
            showPreview={false}
            footer={
              <div className="p-4 bg-white border-t border-javelin-gray-border w-full flex justify-end rounded-br">
                <button
                  className="btn btn-primary rounded w-28 py-3 px-0 z-50"
                  onClick={_onApply}
                >
                  Apply
                </button>
              </div>
            }
          />
        </div>
      ) : null}
    </div>
  );
};

DateRangePicker.propTypes = {
  range: PropTypes.shape({}),
  onApply: PropTypes.func,
  className: PropTypes.string,
};
DateRangePicker.defaultProps = {
  range: initialRange,
  className: '',
};
export default DateRangePicker;
